import { useEffect, useState } from 'react';
import {
  exposureFor,
  formatExposure,
  formatStrategyId,
  modeForTarget,
  type ConfigControlView,
  type StrategyDraftInput,
} from '../../simulation/config-control.ts';
import { fetchJson, parseApiError } from '../lib/api.ts';
import type { PaperStrategyConfig } from '../../simulation/types.ts';
import { formatInteger, formatUtcStamp } from '../../shared/format.ts';

type DraftRow = {
  id: string | null;
  targetMultiplier: string;
  entryDrought: string;
  stopDrought: string;
  baseStake: string;
};

type PreviewResponse = {
  expectedVersion: number;
  nextVersion: number;
  effectiveAfterSequence: number;
  firstRound: number;
  proposal: {
    diffs: Array<{
      id: string;
      kind: string;
      old: PaperStrategyConfig | null;
      next: PaperStrategyConfig | null;
      oldExposure: number;
      nextExposure: number;
    }>;
  };
};

function rowsFrom(strategies: readonly PaperStrategyConfig[]): DraftRow[] {
  return strategies.map((row) => ({
    id: row.id,
    targetMultiplier: String(row.targetMultiplier),
    entryDrought: String(row.entryDrought),
    stopDrought: String(row.stopDrought),
    baseStake: String(row.baseStake),
  }));
}

function emptyRow(): DraftRow {
  return {
    id: null,
    targetMultiplier: '2',
    entryDrought: '5',
    stopDrought: '11',
    baseStake: '1',
  };
}

function derivedMode(target: string): string {
  const parsed = Number(target);
  if (!Number.isFinite(parsed) || parsed < 2) {
    return '—';
  }
  try {
    return modeForTarget(parsed);
  } catch {
    return '—';
  }
}

export function StrategyConfigEditor(props: {
  open: boolean;
  onClose: () => void;
  onApplied: () => Promise<void>;
}) {
  const [view, setView] = useState<ConfigControlView | null>(null);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [pendingNote, setPendingNote] = useState<string | null>(null);

  const load = async () => {
    const payload = await fetchJson<ConfigControlView>('/api/paper/config');
    setView(payload);
    setRows(rowsFrom(payload.active.strategies));
    setPreview(null);
    setError(null);
  };

  useEffect(() => {
    if (!props.open) {
      return;
    }
    void load().catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : 'Failed to load configuration');
    });
  }, [props.open]);

  if (!props.open) {
    return null;
  }

  function updateRow(index: number, key: keyof DraftRow, value: string) {
    setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)));
    setPreview(null);
  }

  const inputs: StrategyDraftInput[] = rows.map((row) => ({
    id: row.id,
    targetMultiplier: row.targetMultiplier,
    entryDrought: row.entryDrought,
    stopDrought: row.stopDrought,
    baseStake: row.baseStake,
  }));

  async function review() {
    if (!view) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = await fetchJson<PreviewResponse>('/api/paper/config/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expectedVersion: view.active.version, strategies: inputs }),
      });
      setPreview(payload);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : parseApiError(cause));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!view || !preview) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const applied = await fetchJson<ConfigControlView>('/api/paper/config/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expectedVersion: view.active.version,
          strategies: inputs,
          confirm: true,
        }),
      });
      setPendingNote(
        `CONFIG CHANGE PENDING. Effective after seq ${applied.pending?.effectiveAfterSequence ?? preview.effectiveAfterSequence}.`,
      );
      setPreview(null);
      await props.onApplied();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : parseApiError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-20 flex items-start justify-center overflow-auto bg-black/40 p-4">
      <div className="w-full max-w-4xl rounded border border-line bg-panel p-4 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Edit strategies</h2>
            <p className="text-xs text-muted">
              Add or remove any number of strategies. Mode is derived from target. 2x = MARTINGALE, greater than 2x = FIXED.
            </p>
          </div>
          <button type="button" className="text-sm" onClick={props.onClose}>
            Close
          </button>
        </div>
        {error ? <p className="mt-3 text-sm text-negative">{error}</p> : null}
        {pendingNote ? <p className="mt-3 text-sm text-warn">{pendingNote}</p> : null}
        <div className="mt-3 table-scroll">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-2 py-1">ID</th>
                <th className="px-2 py-1">Target</th>
                <th className="px-2 py-1">D</th>
                <th className="px-2 py-1">S</th>
                <th className="px-2 py-1">Stake</th>
                <th className="px-2 py-1">Mode</th>
                <th className="px-2 py-1">Exposure</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const mode = derivedMode(row.targetMultiplier);
                let exposure = '—';
                try {
                  const target = Number(row.targetMultiplier);
                  const entry = Number(row.entryDrought);
                  const stop = Number(row.stopDrought);
                  const stake = Number(row.baseStake);
                  if (Number.isFinite(target) && Number.isFinite(entry) && Number.isFinite(stop) && Number.isFinite(stake) && stop > entry) {
                    exposure = formatExposure(
                      exposureFor({
                        id: row.id ?? `draft-${index}`,
                        targetMultiplier: target,
                        entryDrought: entry,
                        stopDrought: stop,
                        stakingMode: mode === 'MARTINGALE' || mode === 'FIXED' ? mode : 'FIXED',
                        baseStake: stake,
                      }),
                    );
                  }
                } catch {
                  exposure = '—';
                }
                return (
                  <tr key={`${row.id ?? 'new'}-${index}`} className="border-t border-line">
                    <td className="px-2 py-1 text-xs text-muted">{row.id ? formatStrategyId(row.id) : 'new'}</td>
                    <td className="px-2 py-1">
                      <input className="w-16 rounded border border-line bg-soft px-1 py-0.5" value={row.targetMultiplier} onChange={(event) => updateRow(index, 'targetMultiplier', event.target.value)} />
                    </td>
                    <td className="px-2 py-1">
                      <input className="w-16 rounded border border-line bg-soft px-1 py-0.5" value={row.entryDrought} onChange={(event) => updateRow(index, 'entryDrought', event.target.value)} />
                    </td>
                    <td className="px-2 py-1">
                      <input className="w-16 rounded border border-line bg-soft px-1 py-0.5" value={row.stopDrought} onChange={(event) => updateRow(index, 'stopDrought', event.target.value)} />
                    </td>
                    <td className="px-2 py-1">
                      <input className="w-16 rounded border border-line bg-soft px-1 py-0.5" value={row.baseStake} onChange={(event) => updateRow(index, 'baseStake', event.target.value)} />
                    </td>
                    <td className="px-2 py-1 text-muted">{mode}</td>
                    <td className="px-2 py-1">{exposure}</td>
                    <td className="px-2 py-1">
                      <button
                        type="button"
                        className="text-xs text-negative"
                        onClick={() => {
                          setRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
                          setPreview(null);
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          className="mt-3 rounded border border-line px-3 py-1.5 text-sm font-semibold"
          onClick={() => {
            setRows((current) => [...current, emptyRow()]);
            setPreview(null);
          }}
        >
          + Add Strategy
        </button>
        {preview ? (
          <div className="mt-4 space-y-2 text-sm">
            <p className="font-semibold">Review changes</p>
            <p className="text-muted">
              Effective after seq {formatInteger(preview.effectiveAfterSequence)} · first new round {formatInteger(preview.firstRound)}
            </p>
            {preview.proposal.diffs
              .filter((row) => row.kind !== 'UNCHANGED')
              .map((row) => (
                <p key={`${row.kind}-${row.id}`}>
                  {row.kind} {formatStrategyId(row.id)}
                  {row.old
                    ? `: ${row.old.targetMultiplier}x D${row.old.entryDrought}-S${row.old.stopDrought} $${row.old.baseStake}`
                    : ''}
                  {row.next
                    ? ` → ${row.next.targetMultiplier}x D${row.next.entryDrought}-S${row.next.stopDrought} $${row.next.baseStake}`
                    : ''}
                </p>
              ))}
            <button type="button" className="rounded border border-accent bg-accent/20 px-3 py-1.5 text-sm font-semibold disabled:opacity-50" disabled={busy} onClick={() => void confirm()}>
              Confirm
            </button>
          </div>
        ) : (
          <button type="button" className="mt-4 rounded border border-line px-3 py-1.5 text-sm font-semibold disabled:opacity-50" disabled={busy} onClick={() => void review()}>
            Review Changes
          </button>
        )}
      </div>
    </div>
  );
}

export function ConfigHistoryDrawer(props: { open: boolean; onClose: () => void }) {
  const [view, setView] = useState<ConfigControlView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.open) {
      return;
    }
    void fetchJson<ConfigControlView>('/api/paper/config')
      .then((payload) => {
        setView(payload);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Failed to load configuration history');
      });
  }, [props.open]);

  if (!props.open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-20 flex items-start justify-center overflow-auto bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded border border-line bg-panel p-4 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-sm font-semibold">Config history</h2>
          <button type="button" className="text-sm" onClick={props.onClose}>
            Close
          </button>
        </div>
        {error ? <p className="mt-3 text-sm text-negative">{error}</p> : null}
        <ul className="mt-3 space-y-3 text-sm">
          {(view?.history ?? []).map((row) => (
            <li key={row.version} className="rounded border border-line p-3">
              <p className="font-medium">
                v{row.version} · {row.source} · {row.status}
              </p>
              <p className="text-xs text-muted">{formatUtcStamp(row.createdAtUtc)}</p>
              <p className="text-xs text-muted">
                Effective after {formatInteger(row.effectiveAfterSequence)}
                {row.effectiveFromSequence != null ? ` · from ${formatInteger(row.effectiveFromSequence)}` : ''}
              </p>
              {row.strategies.map((strategy) => (
                <p key={strategy.id}>
                  {formatStrategyId(strategy.id)} {strategy.targetMultiplier}x D{strategy.entryDrought}-S{strategy.stopDrought} {strategy.stakingMode} ${strategy.baseStake}
                </p>
              ))}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
